import React, { useState, useEffect } from 'react';

interface TrustedTester {
  id: string;
  name: string;
  experienceLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  yearsUsing: string;
  specialties: string[];
  reviewsCompleted: number;
  averageRating: number;
  responseTime: number; // hours
  location: string;
  verified: boolean;
  joinedDate: string;
  lastActive: string;
  bio: string;
}

interface TestingRequest {
  id: string;
  vendorId: string;
  testerId: string;
  productId: string;
  productName: string;
  status: 'pending' | 'accepted' | 'shipped' | 'completed' | 'declined';
  requestDate: string;
  responseDate?: string;
  completionDate?: string;
  trackingNumber?: string;
  reviewId?: string;
  notes?: string;
}

interface VendorTesterDashboardProps {
  vendorId: string;
  subscriptionTier: 'basic' | 'professional' | 'enterprise';
}

export default function VendorTesterDashboard({ vendorId, subscriptionTier }: VendorTesterDashboardProps) {
  const [availableTesters, setAvailableTesters] = useState<TrustedTester[]>([]);
  const [activeRequests, setActiveRequests] = useState<TestingRequest[]>([]);
  const [selectedTesters, setSelectedTesters] = useState<string[]>([]);
  const [searchFilters, setSearchFilters] = useState({
    experienceLevel: '',
    specialties: '',
    location: ''
  });
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Subscription limits
  const getTierLimits = () => {
    switch (subscriptionTier) {
      case 'basic':
        return { maxRequests: 5, maxActiveRequests: 2 };
      case 'professional':
        return { maxRequests: 15, maxActiveRequests: 5 };
      case 'enterprise':
        return { maxRequests: 50, maxActiveRequests: 15 };
      default:
        return { maxRequests: 5, maxActiveRequests: 2 };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Simulate API calls
      setTimeout(() => {
        const mockTesters: TrustedTester[] = [
          {
            id: '1',
            name: 'Sarah M.',
            experienceLevel: 'expert',
            yearsUsing: '5+',
            specialties: ['Pain Management', 'Red Strains', 'Quality Analysis'],
            reviewsCompleted: 47,
            averageRating: 4.9,
            responseTime: 12,
            location: 'California',
            verified: true,
            joinedDate: '2023-03-15',
            lastActive: '2024-01-20',
            bio: 'Former pharmacy tech with extensive kratom experience. Specializes in detailed alkaloid analysis and pain management effects.'
          },
          {
            id: '2',
            name: 'Michael R.',
            experienceLevel: 'advanced',
            yearsUsing: '3-5',
            specialties: ['Energy Strains', 'White/Green Varieties', 'Productivity'],
            reviewsCompleted: 32,
            averageRating: 4.7,
            responseTime: 8,
            location: 'Texas',
            verified: true,
            joinedDate: '2023-06-20',
            lastActive: '2024-01-19',
            bio: 'Business professional who uses kratom for energy and focus. Expert in white and green strain effects.'
          },
          {
            id: '3',
            name: 'Jennifer L.',
            experienceLevel: 'advanced',
            yearsUsing: '3-5',
            specialties: ['Sleep Aid', 'Anxiety Relief', 'Red Strains'],
            reviewsCompleted: 28,
            averageRating: 4.8,
            responseTime: 16,
            location: 'Florida',
            verified: true,
            joinedDate: '2023-08-10',
            lastActive: '2024-01-18',
            bio: 'Wellness advocate with focus on kratom for anxiety and sleep. Detailed reviewer with medical background.'
          },
          {
            id: '4',
            name: 'David K.',
            experienceLevel: 'expert',
            yearsUsing: '5+',
            specialties: ['All Varieties', 'Quality Control', 'Batch Analysis'],
            reviewsCompleted: 63,
            averageRating: 4.9,
            responseTime: 6,
            location: 'Oregon',
            verified: true,
            joinedDate: '2022-12-05',
            lastActive: '2024-01-20',
            bio: 'Long-time kratom enthusiast with comprehensive knowledge across all strain types. Quality-focused reviewer.'
          }
        ];

        const mockRequests: TestingRequest[] = [
          {
            id: '1',
            vendorId: vendorId,
            testerId: '1',
            productId: 'prod-001',
            productName: 'Premium Red Maeng Da',
            status: 'shipped',
            requestDate: '2024-01-15',
            responseDate: '2024-01-16',
            trackingNumber: 'TRK123456789',
            notes: 'Looking for detailed pain management effects review'
          },
          {
            id: '2',
            vendorId: vendorId,
            testerId: '3',
            productId: 'prod-002',
            productName: 'Green Thai Kratom',
            status: 'accepted',
            requestDate: '2024-01-18',
            responseDate: '2024-01-18',
            notes: 'Need review focused on anxiety relief properties'
          }
        ];

        setAvailableTesters(mockTesters);
        setActiveRequests(mockRequests);
        setLoading(false);
      }, 1000);
    };

    fetchData();
  }, [vendorId]);

  const filteredTesters = availableTesters.filter(tester => {
    if (searchFilters.experienceLevel && tester.experienceLevel !== searchFilters.experienceLevel) return false;
    if (searchFilters.specialties && !tester.specialties.some(s => s.toLowerCase().includes(searchFilters.specialties.toLowerCase()))) return false;
    if (searchFilters.location && !tester.location.toLowerCase().includes(searchFilters.location.toLowerCase())) return false;
    return true;
  });

  const handleSendRequest = (testerId: string, productName: string) => {
    // Simulate sending request
    const newRequest: TestingRequest = {
      id: Date.now().toString(),
      vendorId: vendorId,
      testerId: testerId,
      productId: 'temp-id',
      productName: productName,
      status: 'pending',
      requestDate: new Date().toISOString().split('T')[0],
      notes: 'Testing request sent via dashboard'
    };

    setActiveRequests(prev => [...prev, newRequest]);
    alert(`Testing request sent to ${availableTesters.find(t => t.id === testerId)?.name}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'accepted': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'completed': return '#10b981';
      case 'declined': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const tierLimits = getTierLimits();
  const currentActiveRequests = activeRequests.filter(r => ['pending', 'accepted', 'shipped'].includes(r.status)).length;

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading Trusted Testers...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1f2937' }}>
          Trusted Tester Connection Hub
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          Connect with verified, ID-checked kratom testers for honest product reviews
        </p>
      </div>

      {/* Usage Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', margin: '0' }}>
            {currentActiveRequests}/{tierLimits.maxActiveRequests}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Active Requests</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669', margin: '0' }}>
            {activeRequests.filter(r => r.status === 'completed').length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Completed Reviews</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b', margin: '0' }}>
            {availableTesters.length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Available Testers</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#8b5cf6', margin: '0', textTransform: 'capitalize' }}>
            {subscriptionTier}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Plan Tier</p>
        </div>
      </div>

      {/* Active Requests */}
      {activeRequests.length > 0 && (
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>Active Testing Requests</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {activeRequests.map(request => {
              const tester = availableTesters.find(t => t.id === request.testerId);
              return (
                <div key={request.id} style={{ 
                  padding: '1rem', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '0.375rem',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
                        {request.productName}
                      </h4>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                        Tester: {tester?.name}
                      </p>
                    </div>
                    <span style={{ 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '0.25rem', 
                      fontSize: '0.75rem', 
                      fontWeight: '600',
                      backgroundColor: getStatusColor(request.status) + '20',
                      color: getStatusColor(request.status),
                      textTransform: 'capitalize'
                    }}>
                      {request.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9ca3af' }}>
                    <span>Requested: {new Date(request.requestDate).toLocaleDateString()}</span>
                    {request.trackingNumber && (
                      <span>Tracking: {request.trackingNumber}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Filters */}
      <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>Find Trusted Testers</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Experience Level
            </label>
            <select
              value={searchFilters.experienceLevel}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, experienceLevel: e.target.value }))}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Specialties
            </label>
            <input
              type="text"
              value={searchFilters.specialties}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, specialties: e.target.value }))}
              placeholder="e.g., Pain Management, Energy"
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Location
            </label>
            <input
              type="text"
              value={searchFilters.location}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, location: e.target.value }))}
              placeholder="State or region"
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>
      </div>

      {/* Available Testers */}
      <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
            Available Trusted Testers ({filteredTesters.length})
          </h3>
          {currentActiveRequests >= tierLimits.maxActiveRequests && (
            <div style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: '#fef2f2', 
              borderRadius: '0.375rem',
              border: '1px solid #fecaca'
            }}>
              <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: '0' }}>
                Maximum active requests reached ({tierLimits.maxActiveRequests})
              </p>
            </div>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
          {filteredTesters.map(tester => (
            <div key={tester.id} style={{ 
              padding: '1.5rem', 
              border: '1px solid #e5e7eb', 
              borderRadius: '0.5rem',
              backgroundColor: '#fafafa'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                    {tester.name}
                    {tester.verified && (
                      <span style={{ 
                        marginLeft: '0.5rem',
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.375rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '0.25rem'
                      }}>
                        ✓ Verified
                      </span>
                    )}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem',
                      textTransform: 'capitalize'
                    }}>
                      {tester.experienceLevel}
                    </span>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {tester.yearsUsing} years
                    </span>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#059669' }}>
                    ★ {tester.averageRating}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {tester.reviewsCompleted} reviews
                  </div>
                </div>
              </div>
              
              <p style={{ color: '#374151', fontSize: '0.875rem', marginBottom: '1rem', lineHeight: '1.4' }}>
                {tester.bio}
              </p>
              
              <div style={{ marginBottom: '1rem' }}>
                <h5 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Specialties:
                </h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {tester.specialties.map(specialty => (
                    <span key={specialty} style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      borderRadius: '0.25rem'
                    }}>
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <div>Response time: ~{tester.responseTime}h</div>
                  <div>Location: {tester.location}</div>
                  <div>Last active: {new Date(tester.lastActive).toLocaleDateString()}</div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  const productName = prompt('Enter product name for testing:');
                  if (productName) {
                    handleSendRequest(tester.id, productName);
                  }
                }}
                disabled={currentActiveRequests >= tierLimits.maxActiveRequests}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: currentActiveRequests >= tierLimits.maxActiveRequests ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: currentActiveRequests >= tierLimits.maxActiveRequests ? 'not-allowed' : 'pointer'
                }}
              >
                {currentActiveRequests >= tierLimits.maxActiveRequests ? 'Request Limit Reached' : 'Send Testing Request'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}